import { useState } from 'react'
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Paper, Select,
  SimpleGrid, Stack, Table, Text, TextInput, Title,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle, IconFileSpreadsheet } from '@tabler/icons-react'
import {
  DESCRIPCION_REPORTE, ESTADOS, ETIQUETA_REPORTE, REPORTES,
  type NombreReporte, type Resumen,
} from '@serkat/shared'
import { api, queryString } from '../../api/cliente'
import { COLOR_ESTADO } from '../../tema/tema'
import { fecha, moneda } from '../../compartido/formato'

interface Columna { clave: string; titulo: string; tipo: string }
interface Datos { columnas: Columna[]; filas: Record<string, unknown>[] }

function Indicador({ valor, etiqueta, color }: { valor: string; etiqueta: string; color?: string }) {
  return (
    <Card withBorder padding="md">
      <Text fz={26} fw={700} lh={1.1} c={color ?? 'petroleo'} ff="monospace">{valor}</Text>
      <Text size="xs" c="dimmed" mt={6} tt="uppercase" lts={0.5}>{etiqueta}</Text>
    </Card>
  )
}

/** Da formato a una celda según el tipo declarado por el reporte. */
function celda(tipo: string, valor: unknown) {
  if (valor === null || valor === undefined || valor === '') return '—'
  switch (tipo) {
    case 'fecha': return fecha(String(valor))
    case 'moneda': return moneda(Number(valor))
    case 'numero': return Number(valor).toFixed(2)
    case 'entero': return String(Number(valor))
    default: return String(valor)
  }
}

export function ReportesPagina() {
  const [reporte, setReporte] = useState<NombreReporte>('trazabilidad')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [estado, setEstado] = useState<string | null>(null)

  const aceptaFiltros = reporte === 'trazabilidad'
  const filtro = aceptaFiltros
    ? { desde: desde || undefined, hasta: hasta || undefined, estado: estado || undefined }
    : {}

  const { data: resumen } = useQuery({
    queryKey: ['reportes', 'resumen'],
    queryFn: () => api.get<Resumen>('/reportes/resumen'),
  })

  const { data, isFetching } = useQuery({
    queryKey: ['reportes', reporte, filtro],
    queryFn: () => api.get<Datos>(`/reportes/${reporte}${queryString(filtro)}`),
    placeholderData: (previo) => previo,
  })

  const urlExcel = `/api/reportes/${reporte}/excel${queryString(filtro)}`

  return (
    <Stack gap="lg">
      <Title order={2}>Reportes</Title>

      {resumen && (
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
            <Indicador
              valor={moneda(resumen.saldoPorPagar)}
              etiqueta="Saldo por pagar"
            />
            <Indicador
              valor={String(resumen.documentosDetenidos)}
              etiqueta="Detenidos 3+ días"
              color={resumen.documentosDetenidos > 0 ? 'orange' : undefined}
            />
            <Indicador
              valor={resumen.diasPromedioAprobacion === null ? '—' : `${resumen.diasPromedioAprobacion}`}
              etiqueta="Días promedio para decidir"
            />
            <Indicador
              valor={String(resumen.proveedoresAprobados)}
              etiqueta="Proveedores homologados"
            />
            <Indicador
              valor={String(resumen.reevaluacionesVencidas)}
              etiqueta="Reevaluaciones vencidas"
              color={resumen.reevaluacionesVencidas > 0 ? 'red' : undefined}
            />
          </SimpleGrid>

          <Group align="flex-start" gap="lg">
            {resumen.documentosPorEstado.length > 0 && (
              <Paper withBorder p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" mb={6}>Documentos por estado</Text>
                <Group gap="xs">
                  {resumen.documentosPorEstado.map((d) => (
                    <Badge key={d.estado} variant="light" color={COLOR_ESTADO[d.estado]} size="lg">
                      {d.estado}: {d.cantidad}
                    </Badge>
                  ))}
                </Group>
              </Paper>
            )}
            {resumen.ordenesPorEmpresa.length > 0 && (
              <Paper withBorder p="sm">
                <Text size="xs" c="dimmed" tt="uppercase" mb={6}>Órdenes por empresa emisora</Text>
                <Group gap="lg">
                  {resumen.ordenesPorEmpresa.map((e) => (
                    <div key={e.empresa}>
                      <Text size="sm" fw={550}>{e.empresa}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {e.cantidad} · {moneda(e.totalPen)}
                      </Text>
                    </div>
                  ))}
                </Group>
              </Paper>
            )}
          </Group>
        </Stack>
      )}

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Select
              label="Reporte"
              data={REPORTES.map((r) => ({ value: r, label: ETIQUETA_REPORTE[r] }))}
              value={reporte}
              onChange={(v) => setReporte((v ?? 'trazabilidad') as NombreReporte)}
              w={320}
            />
            <Button
              component="a" href={urlExcel} variant="light"
              leftSection={<IconFileSpreadsheet size={16} />}
              disabled={(data?.filas.length ?? 0) === 0}
            >
              Descargar en Excel
            </Button>
          </Group>

          <Text size="sm" c="dimmed">{DESCRIPCION_REPORTE[reporte]}</Text>

          {aceptaFiltros && (
            <Group align="flex-end">
              <TextInput label="Desde" type="date" value={desde}
                onChange={(e) => setDesde(e.currentTarget.value)} w={160} />
              <TextInput label="Hasta" type="date" value={hasta}
                onChange={(e) => setHasta(e.currentTarget.value)} w={160} />
              <Select label="Estado" placeholder="Todos" clearable w={170}
                data={ESTADOS.map((e) => ({ value: e, label: e }))}
                value={estado} onChange={setEstado} />
            </Group>
          )}
        </Stack>
      </Card>

      {reporte === 'evaluaciones-vencidas' && (data?.filas.length ?? 0) > 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          Hay {data!.filas.length} proveedor(es) con la reevaluación vencida. Según el
          LOG-FT-04, un proveedor sin reevaluar vigente no debería recibir órdenes nuevas.
        </Alert>
      )}

      {isFetching && !data ? (
        <Loader size="sm" />
      ) : (data?.filas.length ?? 0) === 0 ? (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center">Sin datos para este reporte.</Text>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={900}>
          <Table withTableBorder striped highlightOnHover fz="sm">
            <Table.Thead>
              <Table.Tr>
                {data!.columnas.map((c) => (
                  <Table.Th key={c.clave} style={{ textAlign: c.tipo === 'texto' ? 'left' : 'right' }}>
                    {c.titulo}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data!.filas.map((fila, i) => (
                <Table.Tr key={i}>
                  {data!.columnas.map((c) => (
                    <Table.Td
                      key={c.clave}
                      ff={c.tipo === 'texto' ? undefined : 'monospace'}
                      ta={c.tipo === 'texto' ? undefined : 'right'}
                    >
                      {celda(c.tipo, fila[c.clave])}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Text size="xs" c="dimmed">
        El archivo de Excel incluye quién lo generó, cuándo y con qué filtros.{' '}
        <Anchor href={urlExcel} size="xs">Descargar</Anchor>
      </Text>
    </Stack>
  )
}
